import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { store } from './store';
import AppRoutes from './routes';
import { AgeConsentGate, ToastContainer } from './components/common';
import { useAppSelector, useAppDispatch } from './hooks/redux';
import { removeToast } from './store/slices/uiSlice';
import { fetchBalance } from './store/slices/walletSlice';
import { fetchApplicationStatus, resetApplicationStatus } from './store/slices/applicationStatusSlice';
import { fetchWhiteLabelConfig } from './store/slices/whiteLabelSlice';
import { useWhiteLabelTheme } from './hooks/useWhiteLabelTheme';
import { initializeTokenRefresh } from './services/api';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import useContentProtection from './hooks/useContentProtection';
import { GOOGLE_TRANSLATE_LANGUAGE_CODES } from './constants/languages';
import {
  AUTO_GOOGLE_TRANSLATE_SOURCE_LANGUAGE,
  GOOGLE_TRANSLATE_APPLIED_EVENT,
  GOOGLE_TRANSLATE_FAILED_EVENT,
  GOOGLE_TRANSLATE_LANGUAGE_CHANGE_EVENT,
  applyGoogleTranslateTargetLanguage,
  getCurrentGoogleTranslateTargetLanguage,
  getGoogleTranslateCookie,
  resolveGoogleTranslateLanguageCode,
  setGoogleTranslateCookie,
} from './utils/googleTranslate';
import AIChatWidget from './components/AIChatWidget';
import AuctionNotificationSystem from './components/AuctionNotificationSystem';
import { GiftCelebrationOverlay } from './components/gifts/GiftCelebrationOverlay';
import GiftNotificationSystem from './components/gifts/GiftNotificationSystem';
import SubscriptionCheckoutReturnHandler from './components/subscription/SubscriptionCheckoutReturnHandler';
import './App.css';

declare global {
  interface Window {
    google?: any;
    googleTranslateElementInit?: () => void;
  }
}

// Suppress ResizeObserver loop limit exceeded error
const resizeObserverError = (e: any) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
    e.message === 'ResizeObserver loop limit exceeded') {
    const resizeObserverErrGuid = document.getElementById('webpack-dev-server-client-overlay-div');
    const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
    if (resizeObserverErr) resizeObserverErr.style.display = 'none';
    if (resizeObserverErrGuid) resizeObserverErrGuid.style.display = 'none';
    e.stopImmediatePropagation();
  }
};

window.addEventListener('error', resizeObserverError);

const AppContent: React.FC = () => {
  useContentProtection();
  const toasts = useAppSelector((state) => state.ui.toasts);
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const theme = useAppSelector((state) => state.ui.theme);
  const dispatch = useAppDispatch();
  const { settings } = useSettings();
  const appDefaultLanguage = resolveGoogleTranslateLanguageCode(settings?.default_language);
  const sourceLanguage = AUTO_GOOGLE_TRANSLATE_SOURCE_LANGUAGE;
  const preferredTargetLanguage = resolveGoogleTranslateLanguageCode(user?.language_preference || appDefaultLanguage);

  // Apply white label theme
  useWhiteLabelTheme();

  useEffect(() => {
    const syncVisualViewportVars = () => {
      const visualViewport = window.visualViewport;
      const height = visualViewport?.height || window.innerHeight;
      const offsetTop = visualViewport?.offsetTop || 0;
      const root = document.documentElement;

      root.style.setProperty('--app-visual-viewport-height', `${height}px`);
      root.style.setProperty('--app-visual-viewport-offset-top', `${offsetTop}px`);
    };

    syncVisualViewportVars();
    window.visualViewport?.addEventListener('resize', syncVisualViewportVars);
    window.visualViewport?.addEventListener('scroll', syncVisualViewportVars);
    window.addEventListener('resize', syncVisualViewportVars);
    window.addEventListener('orientationchange', syncVisualViewportVars);

    return () => {
      window.visualViewport?.removeEventListener('resize', syncVisualViewportVars);
      window.visualViewport?.removeEventListener('scroll', syncVisualViewportVars);
      window.removeEventListener('resize', syncVisualViewportVars);
      window.removeEventListener('orientationchange', syncVisualViewportVars);
    };
  }, []);

  useEffect(() => {
    const translateContainerId = 'google_translate_element';
    const googleScriptId = 'google-translate-script';
    const includedLanguages = GOOGLE_TRANSLATE_LANGUAGE_CODES.join(',');
    const translateWidgetPageLanguage = appDefaultLanguage;
    let isCancelled = false;
    let isInitialized = false;
    let initIntervalId: number | undefined;
    let syncIntervalId: number | undefined;

    const getDesiredTargetLanguage = () => getCurrentGoogleTranslateTargetLanguage(preferredTargetLanguage);

    document.documentElement.lang = getDesiredTargetLanguage();

    const isSourceLanguageTarget = (targetLanguage: string) => (
      sourceLanguage !== AUTO_GOOGLE_TRANSLATE_SOURCE_LANGUAGE
      && resolveGoogleTranslateLanguageCode(targetLanguage) === sourceLanguage
    );

    const prepareTranslateContainer = () => {
      let container = document.getElementById(translateContainerId);

      if (!container) {
        container = document.createElement('div');
        container.id = translateContainerId;
        document.body.appendChild(container);
      }

      Object.assign(container.style, {
        display: 'block',
        position: 'fixed',
        left: '-9999px',
        top: '0',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        opacity: '0',
        pointerEvents: 'none',
        visibility: 'visible',
        zIndex: '-1',
      });

      return container;
    };

    const dispatchTranslateStatusEvent = (eventName: string, targetLanguage: string) => {
      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail: {
            sourceLanguage,
            targetLanguage,
          },
        })
      );
    };

    const syncSavedLanguageToWidget = () => {
      const select = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
      if (!select) {
        return false;
      }

      const cookie = getGoogleTranslateCookie();
      const compatibleLanguage = resolveGoogleTranslateLanguageCode(
        cookie?.targetLanguage || getDesiredTargetLanguage()
      );

      if (!Array.from(select.options).some((option) => option.value === compatibleLanguage)) {
        return false;
      }

      select.value = compatibleLanguage;
      select.dispatchEvent(new Event('change'));
      setGoogleTranslateCookie(sourceLanguage, compatibleLanguage);
      document.documentElement.lang = compatibleLanguage;
      return true;
    };

    const retrySyncSavedLanguageToWidget = () => {
      if (syncIntervalId) {
        window.clearInterval(syncIntervalId);
      }

      let syncAttempts = 0;
      syncIntervalId = window.setInterval(() => {
        syncAttempts += 1;
        const targetLanguage = getDesiredTargetLanguage();
        const didApply = syncSavedLanguageToWidget();
        const isAlreadySourceLanguage = isSourceLanguageTarget(targetLanguage);

        if (didApply || isAlreadySourceLanguage || syncAttempts >= 120) {
          if (syncIntervalId) {
            window.clearInterval(syncIntervalId);
            syncIntervalId = undefined;
          }

          window.dispatchEvent(new Event('google-translate-ready'));
          dispatchTranslateStatusEvent(
            didApply || isAlreadySourceLanguage ? GOOGLE_TRANSLATE_APPLIED_EVENT : GOOGLE_TRANSLATE_FAILED_EVENT,
            targetLanguage
          );
        }
      }, 250);
    };

    const initializeGoogleTranslate = () => {
      if (isCancelled || isInitialized || !window.google?.translate?.TranslateElement) {
        return;
      }

      isInitialized = true;

      // The Google Translate widget is a document-level singleton. This effect re-runs
      // whenever the source/target language deps change (e.g. once the user object
      // loads after login), and recreating the widget each time spawns duplicate
      // widgets and extra flicker. Construct it only once; on later runs just re-apply
      // the saved language to the existing widget.
      if ((window as any).__b4bGoogleTranslateWidgetReady) {
        retrySyncSavedLanguageToWidget();
        return;
      }

      const container = prepareTranslateContainer();

      container.innerHTML = '';
      setGoogleTranslateCookie(
        sourceLanguage,
        getDesiredTargetLanguage()
      );
      new window.google.translate.TranslateElement({
        pageLanguage: translateWidgetPageLanguage,
        includedLanguages,
        multilanguagePage: true,
        autoDisplay: false,
      }, translateContainerId);

      (window as any).__b4bGoogleTranslateWidgetReady = true;
      retrySyncSavedLanguageToWidget();
    };

    window.googleTranslateElementInit = initializeGoogleTranslate;

    if (window.google?.translate?.TranslateElement) {
      initializeGoogleTranslate();
    } else {
      const existingScript = document.getElementById(googleScriptId) as HTMLScriptElement | null;

      if (!existingScript) {
        const script = document.createElement('script');
        script.id = googleScriptId;
        script.type = 'text/javascript';
        script.async = true;
        script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.onload = () => window.setTimeout(initializeGoogleTranslate, 0);
        script.onerror = () => {
          const targetLanguage = getDesiredTargetLanguage();
          dispatchTranslateStatusEvent(
            isSourceLanguageTarget(targetLanguage) ? GOOGLE_TRANSLATE_APPLIED_EVENT : GOOGLE_TRANSLATE_FAILED_EVENT,
            targetLanguage
          );
        };
        document.body.appendChild(script);
      }

      initIntervalId = window.setInterval(() => {
        if (window.google?.translate?.TranslateElement) {
          window.clearInterval(initIntervalId);
          initializeGoogleTranslate();
        }
      }, 300);
    }

    const handleRequestedLanguageChange = () => {
      document.documentElement.lang = getDesiredTargetLanguage();
      retrySyncSavedLanguageToWidget();
    };

    window.addEventListener(GOOGLE_TRANSLATE_LANGUAGE_CHANGE_EVENT, handleRequestedLanguageChange);

    return () => {
      isCancelled = true;

      if (initIntervalId) {
        window.clearInterval(initIntervalId);
      }

      if (syncIntervalId) {
        window.clearInterval(syncIntervalId);
      }

      window.removeEventListener(GOOGLE_TRANSLATE_LANGUAGE_CHANGE_EVENT, handleRequestedLanguageChange);
    };
  }, [appDefaultLanguage, sourceLanguage, preferredTargetLanguage]);

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) {
      return;
    }

    let debounceId: number | undefined;
    let isApplyingTranslation = false;

    const applyCurrentTranslation = () => {
      const select = document.querySelector('.goog-te-combo') as HTMLSelectElement | null;
      if (!select || select.options.length === 0) {
        return;
      }

      const targetLanguage = getCurrentGoogleTranslateTargetLanguage(preferredTargetLanguage);
      document.documentElement.lang = targetLanguage;

      // Idempotency guard — the heart of the fix.
      // If the Google Translate widget is already on the desired language, do NOT
      // re-dispatch a `change` event. Google reacts to `change` by first resetting
      // the page back to the source language and then re-translating it, which on a
      // constantly-updating SPA produces an endless "flash back to English" loop.
      // Google's own internal observer keeps newly added content translated, so we
      // only need to act when the widget has genuinely drifted off the target.
      if (resolveGoogleTranslateLanguageCode(select.value) === targetLanguage) {
        return;
      }

      if (isApplyingTranslation) {
        return;
      }

      isApplyingTranslation = true;
      applyGoogleTranslateTargetLanguage(
        targetLanguage,
        sourceLanguage
      );

      window.setTimeout(() => {
        isApplyingTranslation = false;
      }, 1000);
    };

    const scheduleTranslationRefresh = () => {
      if (isApplyingTranslation) {
        return;
      }

      if (debounceId) {
        window.clearTimeout(debounceId);
      }

      debounceId = window.setTimeout(applyCurrentTranslation, 600);
    };

    const shouldIgnoreNode = (node: Node): boolean => {
      const element = node instanceof Element ? node : node.parentElement;
      if (!element) {
        return true;
      }

      // Google Translate wraps every translated text run in a <font> element. Those
      // wrappers (and Google's own widget chrome) must be ignored, otherwise the
      // translation output itself is mistaken for fresh page content and triggers a
      // re-translation loop.
      if (element.tagName === 'FONT') {
        return true;
      }

      return Boolean(
        element.closest('#google_translate_element, .skiptranslate, font, script, style, textarea, input, select, option')
      );
    };

    const observer = new MutationObserver((mutations) => {
      const hasVisibleContentChange = mutations.some((mutation) => {
        if (shouldIgnoreNode(mutation.target)) {
          return false;
        }

        if (mutation.type === 'characterData') {
          return Boolean(mutation.target.textContent?.trim());
        }

        return Array.from(mutation.addedNodes).some((node) => (
          !shouldIgnoreNode(node) && Boolean(node.textContent?.trim())
        ));
      });

      if (hasVisibleContentChange) {
        scheduleTranslationRefresh();
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    window.addEventListener('google-translate-ready', scheduleTranslationRefresh);
    window.addEventListener(GOOGLE_TRANSLATE_LANGUAGE_CHANGE_EVENT, scheduleTranslationRefresh);
    scheduleTranslationRefresh();

    return () => {
      observer.disconnect();
      window.removeEventListener('google-translate-ready', scheduleTranslationRefresh);
      window.removeEventListener(GOOGLE_TRANSLATE_LANGUAGE_CHANGE_EVENT, scheduleTranslationRefresh);

      if (debounceId) {
        window.clearTimeout(debounceId);
      }
    };
  }, [sourceLanguage, preferredTargetLanguage]);

  useEffect(() => {
    const editableSelector = 'input, textarea, [contenteditable="true"], [contenteditable=""], [role="textbox"]';

    const markEditableElement = (element: Element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      element.setAttribute('translate', 'no');
      element.classList.add('notranslate');
    };

    const markEditableElements = (root: ParentNode) => {
      if (root instanceof Element && root.matches(editableSelector)) {
        markEditableElement(root);
      }

      root.querySelectorAll?.(editableSelector).forEach(markEditableElement);
    };

    markEditableElements(document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            markEditableElements(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  // Apply theme based on user preference
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [theme]);

  // Load white label configuration on mount
  useEffect(() => {
    dispatch(fetchWhiteLabelConfig());
  }, [dispatch]);

  // Validate token and fetch user data on mount
  useEffect(() => {
    const validateAuth = async () => {
      const token = localStorage.getItem('accessToken');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser && !user) {
        // Token exists but user not in state - restore from localStorage
        try {
          const userData = JSON.parse(storedUser);
          dispatch({ type: 'auth/setUser', payload: userData });
          dispatch(fetchBalance());
        } catch (error) {
          console.error('Failed to restore user session:', error);
          // Clear invalid data
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
      } else if (isAuthenticated && user) {
        // User is authenticated, fetch latest data
        dispatch(fetchBalance());
      }
    };

    validateAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Fetch wallet balance when authentication state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(fetchBalance());
    }
  }, [isAuthenticated, user, dispatch]);

  // Fetch application status once when user or model logs in — cached in Redux
  // MODEL users (manager-created) need this to check if they've submitted KYC
  useEffect(() => {
    if (isAuthenticated && (user?.role === 'user' || user?.role === 'model')) {
      dispatch(fetchApplicationStatus());
    } else if (!isAuthenticated) {
      dispatch(resetApplicationStatus());
    }
  }, [isAuthenticated, user?.role, dispatch]);

  // Proactive token refresh - initialize smart token refresh scheduler
  // This will automatically refresh tokens before they expire
  useEffect(() => {
    if (isAuthenticated) {
      // Initialize the proactive token refresh scheduler
      initializeTokenRefresh();
    }
  }, [isAuthenticated]);

  const bgUploads = useAppSelector((state) => state.ui.bgUploads);

  const handleCloseToast = (id: string) => {
    dispatch(removeToast(id));
  };

  return (
    <>
      <AppRoutes />
      <ToastContainer toasts={toasts} removeToast={handleCloseToast} />

      {/* Global video upload progress — visible above mobile navigation without blocking app controls. */}
      <AnimatePresence>
        {bgUploads.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 80 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-[calc(5.75rem+env(safe-area-inset-bottom,0px))] left-3 right-3 z-40 flex max-w-[calc(100vw-1.5rem)] flex-col gap-2 pointer-events-none sm:bottom-6 sm:left-auto sm:right-6 sm:w-80"
          >
            {bgUploads.map((upload) => (
              <div
                key={upload.id}
                className="bg-white/95 dark:bg-slate-800/95 backdrop-blur rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-3 flex items-center gap-3 pointer-events-auto"
              >
                {/* Spinning ring */}
                <div className="relative w-8 h-8 flex-shrink-0">
                  <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
                    <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" strokeWidth="3" className="text-slate-200 dark:text-slate-700" />
                    <motion.circle
                      cx="16" cy="16" r="12" fill="none" stroke="currentColor" strokeWidth="3"
                      strokeLinecap="round"
                      className="text-emerald-500"
                      strokeDasharray={`${2 * Math.PI * 12}`}
                      animate={{ strokeDashoffset: 2 * Math.PI * 12 * (1 - upload.progress / 100) }}
                      transition={{ duration: 0.4 }}
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                    {upload.progress}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">Uploading video…</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{upload.label}</p>
                  <div className="mt-1 h-1 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-emerald-500 rounded-full"
                      animate={{ width: `${upload.progress}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Auction winner & creator payment modals + sounds — global, appears on every page */}
      <AuctionNotificationSystem />

      {/* Gift celebration — emoji burst + chime when a gift is sent/received */}
      <GiftCelebrationOverlay />
      <GiftNotificationSystem />
      <SubscriptionCheckoutReturnHandler />

      {/* AI Support Chat Widget — only for authenticated users */}
      {isAuthenticated && <AIChatWidget />}

      <AgeConsentGate />
    </>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <SettingsProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AppContent />
        </BrowserRouter>
      </SettingsProvider>
    </Provider>
  );
};

export default App;
